import { mediaRef } from '@/services/mediaAssets';
import attendanceUtilities from '@/lib/traditions/attendance.cjs';
import { chickFilAFridayAttendance } from '@/data/traditions/chickFilAFridayAttendance';

const movieNightImage = mediaRef('adventures/movie-night/reckon-crew-alamo-drafthouse-cinematic-illustrated.png');
const reckonBallCoverImage = mediaRef('adventures/reckon-ball-cover.png');
const anniversaryDinnerCoverImage = mediaRef('adventures/anniversary-dinner-cover.png');
const chickFilAFridayCoverImage = mediaRef('adventures/chick-fil-a-friday-cover.png');
const happyHoursCoverImage = mediaRef('adventures/happy-hours-cover.png');
const endOfYearLunchCoverImage = mediaRef('adventures/end-of-year-lunch-cover.png');

const dcPrimePlace = {
  name: 'DC Prime',
  city: 'Ashburn',
  state: 'VA',
  description: 'The steakhouse that grew from an early four-founder milestone dinner into a place the expanding Reckon crew kept choosing for the same simple reasons: great food, great service, and time together around the table.',
  storySections: [
    {
      heading: 'When There Were Four of Us',
      paragraphs: [
        'Before the anniversary dinners, before the bigger company events, and before there was much of a Reckon crew to gather around a table, there were four of us.',
        'Marcus.',
        'Brandon A.',
        'Brandon L.',
        'And me.',
        'Early on, we crossed one of those milestone total dollar amounts invoiced that probably wouldn’t mean much to anyone outside a young company.',
        'It wasn’t some massive contract award or company-changing announcement.',
        'But when you’re building something from the beginning, those milestones don’t feel small.',
        'Every invoice represented work we’d won, people we’d put to work, and another little bit of evidence that this thing we were trying to build was actually working.',
        'So I decided we should celebrate.',
        'I took the four of us to DC Prime.',
      ],
    },
    {
      heading: 'Our Place',
      paragraphs: [
        'There wasn’t some grand plan behind choosing it.',
        'We went out for a good steak dinner to celebrate something that mattered to us.',
        'But we loved it.',
        'The steaks were great. The service was great. The atmosphere was right.',
        'So we went back.',
        'And then we went back again.',
        'Over time, DC Prime became more than just a restaurant we liked.',
        'It became our place.',
      ],
    },
    {
      heading: 'The Table Got Bigger',
      paragraphs: [
        'The nice thing was that as Reckon grew, DC Prime grew with us.',
        'Eventually, dinners weren’t just Marcus, Brandon A., Brandon L., and me anymore.',
        'There were more people around the table.',
        'And they seemed to like DC Prime for exactly the same reasons we did.',
        'There didn’t need to be some elaborate explanation for why we kept coming back.',
        'It’s a great steakhouse.',
        'The food is consistently good.',
        'The service makes the night feel special.',
        'And it’s the kind of place where you can settle in around a table and spend an evening together.',
        'Eventually, it became a natural choice for something bigger.',
        'Our anniversary dinners.',
      ],
    },
    {
      heading: 'Where We Go Back',
      paragraphs: [
        'That’s what makes DC Prime different from most of the restaurants we’ve discovered while traveling.',
        'In Las Vegas, I’m constantly searching for what’s new.',
        'What’s generating buzz?',
        'What haven’t we tried?',
        'DC Prime is almost the opposite.',
        'We don’t need something new.',
        'We already found the place we want to go.',
        'It started with four of us sitting around a table celebrating a milestone total dollar amount invoiced and one of the first signs that what we were building was actually working.',
        'Reckon got bigger.',
        'The milestones got bigger.',
        'And eventually, so did the table.',
        'But we kept coming back to the same place.',
        'Sometimes that’s how a tradition starts.',
        'Nobody declares it one.',
        'You just keep going back.',
      ],
    },
  ],
};

const flemingsPrimePlace = {
  name: "Fleming's Prime Steakhouse & Wine Bar",
  city: 'Tysons',
  state: 'VA',
  description: 'The 2025 Anniversary Dinner took the tradition to a new table in Tysons, making the celebration more convenient for a different part of the growing Reckon crew.',
  storySections: [
    {
      heading: 'Taking the Tradition Somewhere New',
      paragraphs: [
        'By 2025, the Reckon anniversary dinner had become a tradition.',
        'DC Prime had been part of that history from the early days, going all the way back to when there were only four of us celebrating one of our first meaningful company milestones.',
        'But traditions don’t necessarily have to mean doing exactly the same thing every year.',
        'We wanted to try something new.',
        'And there was also a practical consideration.',
        'Our crew was spread around Northern Virginia. Some people lived closer to DC Prime. Others lived closer to Tysons.',
        'So we figured maybe the fair thing was to move the anniversary dinner around.',
        'One year, one side of town gets the easier drive.',
        'The next year, the other side does.',
        'In 2025, that meant Fleming’s in Tysons.',
      ],
    },
    {
      heading: 'Same Tradition, Different Table',
      paragraphs: [
        'There was something nice about changing the location without changing what the night was really about.',
        'The anniversary dinner was never supposed to be about a particular steakhouse.',
        'It was about getting everyone together.',
        'Another year of Reckon.',
        'Another year of growth.',
        'Another reason to stop for an evening and recognize what we’d built together.',
        'Fleming’s gave us a new place to do that.',
        'It was convenient for a different part of the crew, gave everyone a restaurant we hadn’t done together before, and still had the kind of steakhouse atmosphere that fit the occasion.',
        'DC Prime was still our place.',
        'We were just taking the tradition somewhere new.',
      ],
    },
    {
      heading: 'Maybe That’s the Tradition',
      paragraphs: [
        'I think that’s actually the more interesting idea.',
        'The Reckon anniversary dinner doesn’t have to belong to one restaurant.',
        'Maybe the tradition is the table.',
        'As the company changes, the restaurant can change with it.',
        'We can go back to DC Prime because of everything it means to our history.',
        'We can go to Fleming’s because it’s someone else’s turn to have the shorter drive.',
        'We can try somewhere new because that’s fun too.',
        'The important part is that once a year, we get everyone around the same table and celebrate another year of Reckon.',
        'In 2025, that table happened to be at Fleming’s.',
        'And for some of the crew, thankfully, it was a little closer to home.',
      ],
    },
  ],
};

const fogoDeChaoPlace = {
  id: 'fogo-de-chao-reston',
  slug: 'fogo-de-chao',
  name: 'Fogo de Chão',
  city: 'Reston',
  state: 'Virginia',
  websiteUrl: 'https://fogodechao.com/location/reston/',
  description: 'The Reston gathering place for a year-end lunch tradition Kevin intentionally brought to Reckon, changing the restaurant but preserving the chance to close the year together.',
  storySections: [
    {
      heading: 'A Tradition Worth Bringing Along',
      paragraphs: [
        'Not every Reckon tradition actually started at Reckon.',
        'At my previous company, we had a tradition of getting everyone together for lunch at Texas de Brazil during the week between Christmas and New Year’s.',
        'I always liked it.',
        'Most of the year’s work was behind us. The holidays were underway. Things were a little quieter, and it gave everyone a chance to get together for one last meal before the calendar turned over.',
        'So when we started Reckon, it was one of the traditions I decided to bring with me.',
        'With one change.',
        'I upgraded us to Fogo de Chão.',
        'At least, I consider it an upgrade.',
        'I always thought Fogo was better.',
        'So the Texas de Brazil tradition became a Reckon tradition at Fogo.',
      ],
    },
    {
      heading: 'The Week Between',
      paragraphs: [
        'There’s something about that week between Christmas and New Year’s that makes this lunch work.',
        'The rush leading into Christmas is over.',
        'The next year hasn’t started yet.',
        'People are taking leave. Customers are quieter. Calendars look different than they do during the rest of the year.',
        'For a little while, everything slows down.',
        'So we get together and eat.',
        'And Fogo is particularly well suited for that.',
        'There’s no elaborate tasting menu to plan and no need for everyone to decide what they want before the meal really gets going.',
        'You sit down.',
        'The table fills up.',
        'And then the meat starts coming.',
        'And keeps coming.',
        'And keeps coming.',
        'Which, admittedly, is a slightly dangerous format for a Reckon event.',
      ],
    },
    {
      heading: 'One More Meal',
      paragraphs: [
        'The end-of-year lunch isn’t an awards ceremony.',
        'It isn’t a planning meeting.',
        'And it doesn’t need an agenda.',
        'That’s kind of the point.',
        'It’s simply one more opportunity to get everyone around the same table before one year ends and another begins.',
        'And I think that’s why it was a tradition worth bringing with me when we started Reckon.',
        'Some parts changed.',
        'Texas de Brazil became Fogo de Chão.',
        'A different company gathered around the table.',
        'Over time, that table got bigger.',
        'But the basic idea stayed exactly the same.',
        'Find a day during that quiet week between Christmas and New Year’s.',
        'Get everyone together.',
        'Eat an unreasonable amount of meat.',
        'And close out another year.',
        'Some Reckon traditions were created along the way.',
        'This one came with us.',
      ],
    },
  ],
};

export const chickFilAFriday = {
  id: 'chick-fil-a-friday',
  slug: 'chick-fil-a-friday',
  contentType: 'tradition',
  name: 'Chick-fil-A Friday',
  shortName: 'Chick-fil-A Friday',
  eyebrow: 'Weekly Tradition',
  heroImage: chickFilAFridayCoverImage,
  summary: "A weekly walk, a shared lunch, and one of Reckon's longest-running office traditions.",
  description: 'It started with a simple Friday lunch run. When the crew is working from the office, we walk together to Chick-fil-A, order lunch, and carry it back to our desks. Over time, the walk became as much a part of the tradition as the meal itself: a chance to step away from the screen, catch up, and close out the week together. Since September 2024, Chick-fil-A Friday has become one of the most consistent rituals in the Reckon office.',
  storySections: [
    {
      heading: 'Free Chicken Works',
      paragraphs: [
        'Chick-fil-A Friday wasn’t an accident.',
        'I was looking for an easy way to incentivize everyone to spend a little time together each week.',
        'Nothing formal.',
        'No scheduled team-building exercise.',
        'No meeting.',
        'Just a few minutes away from the office where people could walk together, talk about whatever was going on, and get a little face time with people they might otherwise spend the entire day sitting near without really talking to.',
        'The incentive didn’t need to be complicated.',
        'Free chicken worked.',
        'So on September 20, 2024, we started Chick-fil-A Friday.',
      ],
    },
    {
      heading: 'Waiting for Brandon',
      paragraphs: [
        'The routine became pretty simple.',
        'Friday rolls around.',
        'Eventually someone starts figuring out who’s going.',
        'And then, more often than not, we wait for Brandon A.’s 11:00 meeting to end.',
        'That became part of the ritual too.',
        'Once everyone is free, we head out together.',
        'The whole thing usually takes about 30 minutes.',
        'Sometimes longer.',
        'Walk over.',
        'Stand in line.',
        'Figure out everyone’s order.',
        'Give whoever is ordering a hard time about the increasingly complicated requests.',
        'Wait for the food.',
        'Then walk back together.',
        'We don’t even eat at Chick-fil-A.',
        'We bring everything back and eat at our desks.',
        'Which means the part I actually wanted when we started doing this isn’t lunch.',
        'It’s the walk.',
      ],
    },
    {
      heading: 'Thirty Minutes',
      paragraphs: [
        'There’s something different about conversation when you’re walking somewhere together.',
        'Nobody has called a meeting.',
        'There’s no agenda.',
        'There isn’t a Teams screen between you.',
        'You’re just walking to get lunch.',
        'People talk about work.',
        'They talk about their weekend.',
        'Someone complains about something.',
        'Someone tells a story.',
        'Someone gets made fun of for their Chick-fil-A order.',
        'Then everyone walks back.',
        'That’s it.',
        'Thirty minutes.',
        'But do it every Friday and those minutes start adding up.',
      ],
    },
    {
      heading: 'They Know Us Now',
      paragraphs: [
        'Eventually we did it enough that the employees at Chick-fil-A started recognizing us.',
        'Which probably says something about how committed we became to the experiment.',
        'What began as a simple idea to get people away from their desks for a few minutes became something nobody really had to organize anymore.',
        'It was just Friday.',
        'And Friday meant Chick-fil-A.',
      ],
    },
    {
      heading: 'It Was Never Really About the Chicken',
      paragraphs: [
        'Some of the meals that became part of The Reckon Way involved private dining rooms, tasting menus, sommeliers, tableside service, and restaurants we’d traveled thousands of miles to experience.',
        'Chick-fil-A Friday has none of that.',
        'We don’t even eat there.',
        'But we’ve probably spent more time together walking back and forth to Chick-fil-A than we have around the tables of any of those restaurants.',
        'That’s why it belongs here.',
        'The chicken was just the incentive.',
        'The walk was the point.',
        'A few minutes away from the office.',
        'A little face time.',
        'A little conversation.',
        'Every Friday.',
        'Sometimes building a culture takes a corporate retreat on the other side of the world.',
        'Sometimes it just takes free chicken.',
      ],
    },
  ],
  cadence: 'weekly',
  status: 'active',
  startDate: '2024-09-20',
  occurrenceCount: 89,
  nextMilestone: 100,
  defaultPlace: { name: 'Chick-fil-A' },
  milestones: [
    { count: 1, label: 'First Chick-fil-A Friday', date: '2024-09-20' },
    { count: 25, label: '25 Fridays Together' },
    { count: 50, label: '50 Fridays Together' },
    { count: 75, label: '75 Fridays Together' },
    { count: 89, label: '89 Confirmed Fridays', date: '2026-08-21' },
    { count: 100, label: '100 Fridays Together', description: 'Next milestone' },
  ],
  tags: ['weekly-tradition', 'office-culture', 'lunch', 'walking', 'team-connection'],
  attendance: attendanceUtilities.sortAttendanceRecords(chickFilAFridayAttendance, 10),
  featured: true,
  seoTitle: 'Chick-fil-A Friday | The Reckon Way',
  seoDescription: 'A weekly Reckon tradition that began on September 20, 2024.',
};

export const anniversaryDinner = {
  id: 'anniversary-dinner',
  slug: 'anniversary-dinner',
  contentType: 'tradition',
  name: 'Anniversary Dinner',
  eyebrow: 'Annual Tradition',
  heroImage: anniversaryDinnerCoverImage,
  summary: 'Each August, the Reckon team and our spouses or dates gather to celebrate another year since the company began. The evening is deliberately simple: cocktails, a seafood tower, a private dining room, and the freedom to order anything on the menu. There are no long speeches or elaborate ceremonies. Just a quick thank-you, a good meal, and the announcement of December’s Reckon Ball.',
  description: 'Every August in Northern Virginia, the Reckon team and our spouses or dates gather for cocktails, a seafood tower, a private dinner, and a simple thank-you for another year together.',
  storySections: [
    {
      heading: 'Marking Another Year',
      paragraphs: [
        'Reckon began in 2022. The following August, we held our first anniversary dinner to mark the company’s first full year.',
        'It became an annual tradition.',
        'Every August, the team and our spouses or dates step away from the normal pace of work and gather for dinner. It gives us a chance to recognize another year of building Reckon together and to share that milestone with the people who join us for the celebration.',
        'The evening is not particularly formal. Most of us arrive directly from work, so the atmosphere stays relaxed even when the setting is a private room at a steakhouse.',
        'The point is not to create an awards banquet or corporate ceremony. It is simply to make time for everyone to sit around the same table and enjoy the milestone.',
      ],
    },
    {
      heading: 'Dinner at DC Prime',
      paragraphs: [
        'DC Prime has become the traditional home of the Reckon anniversary dinner.',
        'We held the first dinner there in August 2023 and returned in 2024. In 2025, we changed the setting and tried Fleming’s. For Reckon’s four-year celebration in August 2026, we returned to DC Prime.',
        'The evening usually begins with cocktails and drinks while everyone arrives and settles in. A seafood tower follows as the shared appetizer before the group moves into a private dining room.',
        'Once seated, everyone is welcome to order anything on the menu.',
        'That freedom is part of the spirit of the evening. The dinner is meant to feel generous without feeling complicated, giving us a chance to enjoy a great meal without turning the celebration into a structured company event.',
      ],
    },
    {
      heading: 'A Quick Thank-You',
      paragraphs: [
        'There is no long presentation during dinner.',
        'At some point, I offer a quick thank-you to the team and everyone who joined us. Then we return to the food, drinks, and conversations happening around the table.',
        'That understated moment is enough. The anniversary dinner is less about recounting every accomplishment from the previous twelve months and more about acknowledging that another year of Reckon happened because of the people in the room.',
        'The evening also includes one piece of company news: we announce the date of the Reckon Ball, our holiday party held each December.',
        'It has become a small bridge between two Reckon traditions. The anniversary dinner pauses to recognize how far we have come since 2022, while announcing the Reckon Ball gives everyone another celebration to anticipate before the year ends.',
      ],
    },
    {
      heading: 'Better Every Year',
      paragraphs: [
        'The structure of the anniversary dinner has remained remarkably consistent: August, the team and their spouses or dates, cocktails, a seafood tower, a private room, and an unhurried meal together.',
        'Yet the dinners have gotten better every year.',
        'The difference is not a more elaborate menu or a bigger celebration. It is that we know one another better. Another year of working together, especially traveling together, gives us more shared experiences, more stories to revisit, and more genuine friendships around the table.',
        'What began as a dinner marking Reckon’s first anniversary has gradually become a reflection of the team itself. The tradition remains familiar, but the relationships within it continue to grow.',
        'That is what we are really celebrating each August.',
      ],
    },
  ],
  cadence: 'annual',
  status: 'active',
  startDate: '2023-08-01',
  occurrenceCount: 4,
  occurrences: [
    {
      id: 'anniversary-dinner-2023', date: '2023-08-17', datePrecision: 'day', title: '2023 Anniversary Dinner', place: dcPrimePlace, summary: 'Private dining at DC Prime.',
    },
    {
      id: 'anniversary-dinner-2024', date: '2024-08-22', datePrecision: 'day', title: '2024 Anniversary Dinner', place: dcPrimePlace, summary: 'The Anniversary Dinner returned to DC Prime.',
    },
    {
      id: 'anniversary-dinner-2025', date: '2025-08-14', datePrecision: 'day', title: '2025 Anniversary Dinner', place: flemingsPrimePlace, summary: "The annual dinner moved to Fleming's Prime in Tysons.",
    },
    {
      id: 'anniversary-dinner-2026', date: '2026-08-13', datePrecision: 'day', title: '2026 Anniversary Dinner', place: dcPrimePlace, summary: 'The Anniversary Dinner returns to DC Prime.',
    },
  ],
  tags: ['annual-tradition', 'company-anniversary', 'private-dining', 'employees-and-guests', 'celebration'],
  featured: true,
};

export const happyHours = {
  id: 'happy-hours',
  slug: 'happy-hours',
  contentType: 'tradition',
  name: 'Happy Hours',
  eyebrow: 'Recurring Tradition',
  heroImage: happyHoursCoverImage,
  summary: 'Reckon happy hours are not regularly scheduled. They happen when someone decides the team needs one, posts a suggestion in Slack, and manages to rally enough people to make it worthwhile. Sometimes that means beer and a quick bite at a familiar local spot. Other times, it becomes a planned competition involving axes, go-karts, and waivers.',
  description: 'There is no standing happy hour on the calendar. It starts with a Slack suggestion, enough thumbs-up reactions to make it happen, and an easy chance for the crew to gather over drinks, food, or friendly competition.',
  storySections: [
    {
      heading: 'How About Tomorrow?',
      paragraphs: [
        'There is no recurring happy hour on the Reckon calendar.',
        'Instead, they usually begin with a simple message in Slack:',
        '“How about a happy hour tomorrow at ___?”',
        'The location changes, but the process is remarkably consistent. People respond with thumbs-up reactions, and if the suggestion attracts enough interest, the happy hour happens.',
        'The spontaneous ones can come together with as little as a day’s notice. There is no invitation process, complicated scheduling exercise, or expectation that everyone must attend. Someone feels like the team could use a happy hour, proposes a place, and sees who is available.',
        'It is happy hour by informal consensus.',
      ],
    },
    {
      heading: 'A Beer or Two',
      paragraphs: [
        'Most Reckon happy hours are simple.',
        'We meet at a local brewery such as Bunnyman, Aslin, Crooked Run, or Ono. Sometimes we choose somewhere like Taco Bamba when a quick bite is as important as the drinks.',
        'The location matters less than finding somewhere easy for the group to gather.',
        'Usually, the evening consists of a beer or two, some food, and time together away from the normal workday. People arrive, catch up, decompress, and head home at a reasonable hour.',
        'Usually.',
        'Every once in a while, one continues later than planned.',
        'That possibility is part of the appeal. There is no agenda and no fixed ending. The gathering can be as brief as the team needs, or continue if everyone is having a good time.',
      ],
    },
    {
      heading: 'When Happy Hour Needs a Waiver',
      paragraphs: [
        'Some happy hours require more than a Slack message and a few thumbs-up reactions.',
        'Outings such as K1 Speed or Axes & O’s need reservations, waivers, and enough advance planning to divide people into race groups or reserve adjacent throwing lanes. They preserve the social character of a happy hour while adding a new activity and some friendly competition.',
        'Those nights produce their own stories: qualifying laps and a winner’s podium at K1, or the difference between a satisfying thunk and a clunk followed by a thud at Axes & O’s.',
        'They take more coordination, but the underlying idea is the same. Someone suggests something that sounds fun, enough people agree, and the crew gets together.',
      ],
    },
    {
      heading: 'When Someone Needs One',
      paragraphs: [
        'Happy hour at Reckon is not an obligation or a standing meeting disguised as fun.',
        'It happens when someone needs one.',
        'That may mean the week has been long, a local brewery sounds particularly appealing, or someone has been lobbying the team to try a new activity. Whatever the reason, the process remains lightweight enough that almost anyone can get one started.',
        'Post the suggestion.',
        'Wait for the thumbs-up reactions.',
        'If enough appear, meet for a beer or two and some food.',
        'And every once in a while, stay later.',
      ],
    },
  ],
  cadence: 'recurring',
  status: 'active',
  startDate: '2023-01-01',
  startDateLabel: 'Pre-2022',
  occurrences: [],
  locations: [
    {
      id: 'crooked-run-sterling',
      name: 'Crooked Run Fermentation',
      city: 'Sterling',
      state: 'Virginia',
      websiteUrl: 'https://www.crookedrunfermentation.com/',
      description: 'Crooked Run Fermentation was one of my favorite happy-hour spots before Reckon existed. What began as a smaller brewery paired with Señor Ramon Taqueria has expanded into a larger part of its Sterling office-park building, adding J&J Pizza and Nectar Cocktail Bar. The place has grown, but the Reckon routine remains simple: open a tab at the bar, wait for everyone to arrive, then line up together and place one large taco order.',
      storySections: [
        {
          heading: 'Before Reckon',
          paragraphs: [
            'My history with Crooked Run goes back to my previous company.',
            'Even then, the Sterling location was a favorite place for happy hour. At the time, the setup was smaller. There was the brewery, which was already producing very good beer, and Señor Ramon Taqueria serving tacos inside.',
            'That combination was enough.',
            'Crooked Run offered beer worth visiting for and food that made it easy for a group to stay. It became one of the traditions that naturally carried over when we started organizing Reckon happy hours.',
            'Some local places enter the rotation because someone suggests trying something new. Crooked Run was already proven.',
          ],
        },
        {
          heading: 'A Larger Space',
          paragraphs: [
            'Crooked Run has expanded since those earlier visits.',
            'The brewery took over a larger portion of its office-park building, creating more room and adding more options under the same roof.',
            'Señor Ramon Taqueria is still there. J&J Pizza has joined the food lineup. Nectar Cocktail Bar gives people who do not prefer beer another reason to come.',
            'That variety makes the current space particularly useful for a group.',
            'Not everyone has to want the same thing. One person can order a beer, someone else can choose a cocktail, and the table can decide between tacos and pizza, or have both.',
            'The expansion gave Crooked Run more ways to accommodate a Reckon happy hour without losing the original brewery and taqueria combination that made it a favorite.',
          ],
        },
        {
          heading: 'Open a Tab',
          paragraphs: [
            'Our visits normally begin at the Crooked Run bar.',
            'We open a tab, order the first beers or cocktails, and find a place for the group to gather. As with most Reckon happy hours, people arrive when work and traffic allow.',
            'There is no need to delay the evening until every person is present. The first arrivals can order a drink and settle in while the rest of the crew makes its way to Sterling.',
            'Once everyone is there, we turn our attention to the tacos.',
          ],
        },
        {
          heading: 'The Taco Line',
          paragraphs: [
            'Rather than trying to collect everyone’s order at the table, the group heads to Señor Ramon together.',
            'Everyone gets in line and orders whatever tacos they want. Those individual choices become one large Reckon order.',
            'It is an efficient system for a group with different tastes. No one has to coordinate a shared menu or remember which taco belongs to whom before ordering. Each person walks through the line, makes a selection, and adds it to the larger order.',
            'Then the tacos arrive, the drinks continue, and the happy hour settles into its usual rhythm.',
          ],
        },
        {
          heading: 'A Favorite That Grew With Us',
          paragraphs: [
            'Crooked Run has changed over the years.',
            'The space is larger. There are more food options. A cocktail bar now makes the location more welcoming to people who would rather skip the beer.',
            'What has not changed is the reason it works.',
            'The beer is good. The food is easy. The Sterling location can accommodate a group, and everyone can find something they want.',
            'It was a favorite happy-hour spot before Reckon began. As the brewery expanded, it became an even better fit for the team we were building.',
            'Now the routine is familiar: start a tab, wait for the crew, line up for tacos, and settle in for a beer or two.',
            'Sometimes longer.',
          ],
        },
      ],
    },
    {
      id: 'ono-brewing-company',
      name: 'Ono Brewing Company',
      city: 'Chantilly',
      state: 'Virginia',
      websiteUrl: 'https://www.onobrewco.com/',
      description: 'Ono Brewing Company became a Reckon happy-hour destination because it offered two things we enjoyed: a self-serve beer wall that made sampling easy and Odd BBQ serving genuinely good food inside the brewery. Odd BBQ has since closed, and a new food concept is on the way, but we expect we will return to see what comes next.',
      storySections: [
        {
          heading: 'Beer by the Ounce',
          paragraphs: [
            'The first thing that distinguishes Ono from a typical brewery is the self-serve tap system.',
            'When you arrive, you receive a card. Each tap has a small card reader mounted above it. To pour a beer, you place the card on the reader, select how much you want, and begin pouring.',
            'The system tracks the number of ounces dispensed, and you pay by the ounce.',
            'It is a simple novelty, but a useful one. Instead of choosing a full pint from an unfamiliar list, everyone can pour a small sample, decide whether they like it, and move on to another tap. People can explore the beer selection at their own pace without waiting for the whole group to order another round.',
            'For a Reckon happy hour, that flexibility works well. Everyone can try something different, compare discoveries, and return to the taps whenever they are ready.',
          ],
        },
        {
          heading: 'The Barbecue Was the Draw',
          paragraphs: [
            'The self-serve beer helped make Ono interesting.',
            'Odd BBQ helped make it a favorite.',
            'For a time, Odd BBQ operated from inside the brewery and gave us more than the usual limited brewery-food options. Everything we tried was good, but one of the early favorites was a grilled cheese that the group loved.',
            'The sandwich eventually disappeared from the menu, but the quality of the rest of the food kept Odd BBQ central to the experience.',
            'That combination made Ono an easy happy-hour choice. People could pour their own beer, order genuinely good barbecue, and settle in without coordinating drinks at one location and dinner somewhere else.',
          ],
        },
        {
          heading: 'A Place in Transition',
          paragraphs: [
            'Odd BBQ has since closed.',
            'That changes part of what originally drew us to Ono. The self-serve tap wall remains, but the brewery and barbecue counter had become linked in our memories of the place. Losing the food partner means the next visit will feel different.',
            'Something new is coming into the space, although we have not tried it yet.',
            'We almost certainly will.',
            'Reckon happy hours are not attached to a rigid list of approved locations. We return to places we enjoy, try new ones when someone makes a suggestion, and adjust as the local brewery scene changes.',
            'Ono earned its place in that rotation with beer by the ounce and barbecue worth returning for. Now we have another reason to go back: to see what the next version becomes.',
          ],
        },
      ],
    },
    {
      id: 'bunnyman-brewing-chantilly',
      name: 'Bunnyman Brewing',
      city: 'Chantilly',
      state: 'Virginia',
      websiteUrl: 'https://bunnymanbrewing.com/',
      description: 'Bunnyman Brewing is one of Reckon’s regular happy-hour stops, but it also serves another purpose. On any given afternoon, there is a good chance Marcus and I will be sitting in the main taproom with our laptops open, working through a back-office project over a pint and something from Eugene’s Sausage and Fries.',
      storySections: [
        {
          heading: 'From Mustang Sally’s to Bunnyman',
          paragraphs: [
            'Before it became Bunnyman Brewing, this location was Mustang Sally Brewing Company.',
            'The ownership and name have changed, but the transition is still fairly recent. So far, Bunnyman has continued serving the same beers that were available under Mustang Sally’s.',
            'That continuity made it easy for us to keep returning. The sign may have changed, but the beer selection and the familiar taproom experience remained in place.',
            'For Reckon, it stayed in the rotation without missing a step.',
          ],
        },
        {
          heading: 'Beer, Sausages, and Fries',
          paragraphs: [
            'The beer is only part of the draw.',
            'Eugene’s Sausage and Fries operates inside the brewery, making Bunnyman an easy place to combine a pint with something more substantial than a typical brewery snack.',
            'The setup works equally well for a spontaneous Reckon happy hour or an afternoon working session. There is no need to coordinate food from another location or leave once people get hungry. Order a pint, pick up something from Eugene’s, and settle in.',
            'It is a simple combination, but a very effective one.',
          ],
        },
        {
          heading: 'Reckon’s Unofficial Back Office',
          paragraphs: [
            'Bunnyman may be the local place where someone is most likely to find Marcus and me with our laptops open.',
            'Normally, we claim a table in the main area, order a pint and something from Eugene’s, and work through whatever back-office project needs attention.',
            'The work itself may not look much different from what we could do in an office or on a video call. The setting changes the pace. A brewery table, food, and a beer make it easier to sit together and work through things without turning the session into another formal meeting.',
            'It is still work.',
            'It is simply work with better refreshments.',
          ],
        },
        {
          heading: 'When Trivia Takes Over',
          paragraphs: [
            'The main taproom does not always remain quiet.',
            'If Bunnyman has an event underway, particularly something like trivia, we move away from the center of the activity and find tables near the pinball machines.',
            'That corner gives us enough separation to keep our laptops open and continue talking through a project while the event carries on elsewhere in the room.',
            'The pinball machines are not exactly a traditional office backdrop, but they have become part of the setting.',
          ],
        },
        {
          heading: 'A Familiar Place Under a New Name',
          paragraphs: [
            'Bunnyman’s chapter in the space is still new.',
            'The beers have not changed yet, Eugene’s remains an important part of the experience, and the taproom continues to work for both happy hours and informal work sessions.',
            'Over time, Bunnyman may put more of its own stamp on the location. The beer list may evolve. Other details may change.',
            'For now, it remains comfortably familiar: a table in the main room, or near the pinball machines when trivia gets loud, two laptops, a couple of pints, food from Eugene’s, and another Reckon back-office project getting worked through.',
          ],
        },
      ],
    },
    {
      id: 'taco-bamba-herndon',
      name: 'Taco Bamba',
      city: 'Herndon',
      state: 'Virginia',
      websiteUrl: 'https://www.tacobamba.com/location/herndon-va/',
      description: 'Taco Bamba in Herndon is one of Reckon’s easiest gathering places. It sits conveniently between Matt’s client location, the rest of the team, and our office space. That makes it a practical meeting point, but the real appeal is even simpler: who does not like tacos and cerveza?',
      storySections: [
        {
          heading: 'A Convenient Middle Ground',
          paragraphs: [
            'Getting a group together in Northern Virginia often comes down to location.',
            'The Herndon Taco Bamba works because it sits between the places Reckon people are already coming from. It is convenient for Matt when he is leaving his client location, close to our office space, and reasonably positioned for most of the rest of the team.',
            'No one needs to travel too far out of the way, and the location is casual enough that a happy hour can come together without much planning.',
            'That makes it an easy answer when someone posts a message in Slack asking whether anyone wants to meet the next day.',
          ],
        },
        {
          heading: 'Claiming a Table',
          paragraphs: [
            'The ideal setup is to find one of the larger tables.',
            'If we can claim enough space for the group, a server takes our food and drink orders at the table. That works especially well when people arrive at slightly different times from work, a client site, or the office.',
            'There is no need for the entire crew to show up at once. People can join the table, order when they are ready, and settle into the conversation already underway.',
            'For a spontaneous Reckon happy hour, that flexibility matters.',
          ],
        },
        {
          heading: 'Tacos, Nachos, and Cerveza',
          paragraphs: [
            'Everyone normally orders their own tacos.',
            'That lets people build whatever combination sounds good without requiring the table to agree on a single meal. Alongside the individual orders, we usually add a few plates of nachos for everyone to share.',
            'Then there are the drinks.',
            'Taco Bamba is not a brewery, but a happy hour does not always need to revolve around a taproom. Sometimes tacos, cerveza, and a large table are exactly what the team needs.',
            'The food arrives, the nachos move around the table, and people stay for a beer or two before heading home.',
            'Every once in a while, as with any Reckon happy hour, the evening lasts a little longer.',
          ],
        },
        {
          heading: 'An Easy Yes',
          paragraphs: [
            'Taco Bamba’s place in the Reckon rotation is not built around a complicated tradition or one unforgettable event.',
            'It works because it is convenient, casual, and broadly appealing.',
            'When someone suggests the Herndon location, the team already knows what the evening will look like. Find a large table. Order individual tacos. Put a few plates of nachos in the middle. Add cerveza.',
            'It is the kind of suggestion that makes collecting enough thumbs-up reactions in Slack fairly easy.',
            'After all, who does not like tacos and cerveza?',
          ],
        },
      ],
    },
    {
      id: 'aslin-beer-herndon',
      name: 'Aslin Beer Company',
      city: 'Herndon',
      state: 'Virginia',
      websiteUrl: 'https://www.aslinbeer.com/herndon',
      description: 'Aslin Beer Company in downtown Herndon is one of Reckon’s regular happy-hour destinations. The shared outdoor space between the brewery and IZZI gives the group plenty of room to gather over beer and pizza. Of all our local brewery stops, Aslin may be my favorite for the beer itself.',
      storySections: [
        {
          heading: 'Downtown Herndon',
          paragraphs: [
            'The downtown Herndon location works well for a Reckon happy hour.',
            'It is easy for the group to reach, casual enough for a gathering organized on short notice, and set up to handle people arriving at different times.',
            'When the weather cooperates, we prefer the outdoor tables between Aslin and IZZI. The shared space connects the brewery and pizza restaurant, giving us access to both without having to move the group from one place to another.',
            'Find a table, open a tab, and the happy hour is underway.',
          ],
        },
        {
          heading: 'One Tab at the Bar',
          paragraphs: [
            'We normally open a single tab at the Aslin bar.',
            'From there, people can walk up and order their own beer on that tab whenever they are ready. No one needs to coordinate a round for the entire table, and later arrivals can settle in without waiting for everyone else.',
            'That flexibility fits the way Reckon happy hours usually happen.',
            'Someone posts a suggestion in Slack. If enough thumbs-up reactions appear, the group meets the following day. People arrive as work and traffic allow, grab a beer, and join the conversation already happening at the table.',
          ],
        },
        {
          heading: 'Pizza by QR Code',
          paragraphs: [
            'Food is just as easy.',
            'QR codes at the outdoor tables allow everyone to order directly from IZZI. There is no need to leave the table, wait in a separate line, or send someone inside to coordinate an order for the group.',
            'Beer comes from Aslin. Pizza comes from IZZI. Both arrive in the same shared space.',
            'It is a simple arrangement, but it gives the location an advantage over a brewery that requires a food truck schedule or an outside delivery order. The food is always close, and people can order when they are hungry.',
          ],
        },
        {
          heading: 'The Beer Is the Reason',
          paragraphs: [
            'The outdoor space and pizza make Aslin convenient.',
            'The beer is what makes it a favorite.',
            'Aslin generally keeps a strong rotation of seasonal releases, so it is difficult to name one beer that defines our visits. The selection changes often enough that there is usually something new to try alongside familiar styles.',
            'That rotating selection is part of the appeal. A favorite from one visit may no longer be available the next time, but something else has taken its place.',
            'There is no single Reckon order at Aslin. People choose whatever sounds good, add it to the open tab, and return to the table.',
          ],
        },
        {
          heading: 'Beer, Pizza, and a Table Outside',
          paragraphs: [
            'Aslin does not require a complicated story.',
            'It earned its place in the Reckon rotation by doing the important things well.',
            'The downtown Herndon location is convenient. The outdoor tables give the group room to gather. IZZI provides pizza through a QR code at the table. The bar lets everyone order independently on a shared tab.',
            'Most importantly, the beer is consistently worth returning for.',
            'When someone suggests Aslin in Slack, it is usually an easy thumbs-up.',
          ],
        },
      ],
    },
  ],
  tags: ['happy-hour', 'after-work', 'team-connection', 'food-and-drink'],
  featured: true,
};

export const reckonBall = {
  id: 'reckon-ball',
  slug: 'reckon-ball',
  contentType: 'tradition',
  name: 'Reckon Ball',
  eyebrow: 'Annual Tradition',
  heroImage: reckonBallCoverImage,
  summary: 'An annual overnight celebration of the Reckon Crew and the guests who support them, built around memorable settings, private dining, and time together outside the office.',
  description: 'Each Reckon Ball has its own setting, personality, and stories, but the purpose remains the same: recognize the people behind our success and create memories together outside the office.',
  cadence: 'annual',
  status: 'active',
  startDate: '2022',
  occurrenceCount: 4,
  occurrences: [],
  archiveRouteName: 'reckon-ball-archive',
  tags: ['annual-tradition', 'overnight-stay', 'private-dining', 'employees-and-guests', 'celebration'],
  featured: true,
};

export const endOfYearLunch = {
  id: 'end-of-year-lunch',
  slug: 'end-of-year-lunch',
  contentType: 'tradition',
  name: 'End of Year Lunch',
  eyebrow: 'Annual Tradition',
  heroImage: endOfYearLunchCoverImage,
  summary: 'A recurring year-end gathering at Fogo de Chão in Reston, bringing the Reckon Crew together around the table before the calendar turns.',
  description: 'The End of Year Lunch gives the crew one more chance to gather, look back on the year, and enjoy an unhurried meal together. Fogo de Chão in Reston makes a natural home for the tradition with its open churrasco grill, tableside fire-roasted cuts, Market Table, and space built for group dining.',
  storySections: fogoDeChaoPlace.storySections,
  cadence: 'annual',
  status: 'active',
  defaultPlace: fogoDeChaoPlace,
  occurrences: [
    {
      id: 'end-of-year-lunch-2023', date: '2023-12-28', datePrecision: 'day', title: '2023 End of Year Lunch', place: fogoDeChaoPlace, summary: 'The crew gathered at Fogo de Chão in Reston.',
    },
    {
      id: 'end-of-year-lunch-2024', date: '2024-01-30', datePrecision: 'day', title: '2024 End of Year Lunch', place: fogoDeChaoPlace, summary: 'The crew gathered at Fogo de Chão in Reston.',
    },
    {
      id: 'end-of-year-lunch-2025', date: '2025-12-31', datePrecision: 'day', title: '2025 End of Year Lunch', place: fogoDeChaoPlace, summary: 'The crew gathered at Fogo de Chão in Reston.',
    },
  ],
  locations: [
    fogoDeChaoPlace,
  ],
  tags: ['annual-tradition', 'end-of-year', 'team-connection', 'lunch', 'celebration'],
  meritBadgeIds: [],
  featured: true,
};

export const newEmployeeLunch = {
  id: 'new-employee-lunch',
  slug: 'new-employee-lunch',
  contentType: 'tradition',
  name: 'New Employee Lunch',
  eyebrow: 'Recurring Tradition',
  summary: 'A welcome lunch for each new Reckon employee, documented through the person joining the crew and the place where everyone gathered.',
  description: 'New Employee Lunch gives the crew an opportunity to step away from work and welcome a new teammate around the table. Each entry keeps the record simple: the new employee’s name, the restaurant, the date when known, and any memory worth carrying forward.',
  storySections: [
    {
      heading: 'Their Pick',
      paragraphs: [
        'Starting a new job comes with plenty of things you don’t get to choose.',
        'New systems.',
        'New customers.',
        'New coworkers.',
        'New processes.',
        'So during someone’s first week at Reckon, we let them make at least one decision.',
        'Where are we having lunch?',
        'The new employee gets to pick the restaurant.',
        'There isn’t a predetermined list of approved places or a restaurant we always go to.',
        'Their first week.',
        'Their choice.',
        'It’s also, perhaps unfairly, their first opportunity to be judged by the entire company.',
        'Choose wisely.',
        'Because before we’ve learned how you work, how you solve problems, or what you’re going to bring to the team, we’re going to learn something potentially just as important.',
        'Where you think we should eat.',
        'No pressure.',
      ],
    },
    {
      heading: 'Making Time for It',
      paragraphs: [
        'Of course, saying everyone is invited and actually making it easy for everyone to attend are two different things.',
        'Especially when you’re working in consulting.',
        'People have customer commitments. They have meetings. They have work that needs to get done.',
        'So Reckon gives everyone 90 minutes of overhead to attend a new employee lunch.',
        'That’s deliberate.',
        'We don’t want someone deciding they can’t go because they need to bill another hour.',
        'We want them there.',
        'The company is essentially saying that spending some time getting to know the newest person on the team is part of the job.',
      ],
    },
    {
      heading: 'Ninety Minutes Without an Agenda',
      paragraphs: [
        'There’s no presentation at lunch.',
        'No onboarding checklist.',
        'No round-robin introductions.',
        'We’re just having lunch.',
        'And that’s exactly why it works.',
        'The new employee gets 90 minutes around a table with the people they’ve just joined, away from desks and meetings and whatever customer work everyone was doing that morning.',
        'They get to hear the conversations.',
        'The jokes.',
        'The stories.',
        'The personalities that don’t necessarily come through in Slack or during a meeting.',
        'And everyone else gets a chance to know them the same way.',
        'Food makes that easy.',
        'There’s always something to talk about, starting with whether the new employee made a good choice.',
      ],
    },
    {
      heading: 'The First Table',
      paragraphs: [
        'We’ve spent a lot of time thinking about the tables that have become part of Reckon’s story.',
        'Anniversary dinners.',
        'End-of-year lunches.',
        'Friday walks to Chick-fil-A.',
        'Restaurants halfway around the world.',
        'But for someone new to Reckon, this might be the first one.',
        'They pick the restaurant.',
        'We provide the time.',
        'Everyone shows up.',
        'And somewhere over those 90 minutes, the new person starts becoming part of the crew.',
        'Of course, we’ve also learned something important about them.',
        'Their taste in restaurants.',
        'First impression made.',
        'Judgment rendered.',
        'Welcome to Reckon.',
      ],
    },
  ],
  cadence: 'recurring',
  status: 'active',
  occurrences: [],
  occurrenceSectionKicker: 'Welcome to the Crew',
  occurrenceSectionTitle: 'New Employee Lunches',
  emptyOccurrencesCopy: 'Add each lunch as a simple entry with the new employee’s name and the restaurant. Dates and short memories can be included whenever they are known.',
  tags: ['new-employee', 'welcome', 'team-lunch', 'team-connection'],
  meritBadgeIds: [],
  featured: true,
};

export const movieNight = {
  id: 'movie-night-alamo',
  slug: 'movie-night-alamo',
  contentType: 'tradition',
  name: 'Movie Night',
  eyebrow: 'Recurring Tradition',
  summary: 'Movie Night began with a title Al believed Reckon could not ignore. What started as a joke around Mission: Impossible – Dead Reckoning became a recurring outing organized by Al, usually at Alamo Drafthouse and occasionally on the giant Airbus IMAX screen at Udvar-Hazy. When the movie is age-appropriate, spouses, partners, and kids join too, turning a team outing into a Reckon family night.',
  description: 'Al organizes Movie Night as an easy, relaxed way for the Reckon crew and their families to gather around dinner, drinks, and a shared big-screen story at Alamo Drafthouse or Udvar-Hazy’s Airbus IMAX Theater.',
  storySections: [
    {
      heading: 'A Movie We Had to See',
      paragraphs: [
        'Al generally runs Reckon Movie Night.',
        'The tradition began with Mission: Impossible – Dead Reckoning. Once Al noticed that “Reckon” appeared in the title, he decided we had to see it.',
        'There was not much room for debate. A major action movie had arrived with our company’s name embedded in its title. Attendance felt less like a suggestion than a corporate obligation.',
        'We saw it at Alamo Drafthouse. The evening worked well enough that Al simply kept suggesting more movies, and the original joke developed into a recurring Reckon tradition.',
      ],
    },
    {
      heading: 'Matt at the Bar',
      paragraphs: [
        'Alamo remains our usual destination because it gives the evening more room to be social than a standard trip to the movies.',
        'The official plan is to meet at the bar an hour before showtime. The unofficial opening scene has become a running joke: Matt arrives first and walks into an empty bar while the rest of us are still fighting our way through Northern Virginia traffic.',
        'Eventually, everyone makes it.',
        'We stay at the bar ordering food and drinks until about ten minutes before the movie begins. Then we head into the theater, settle into our seats, and place our initial food orders before the opening previews give way to the feature.',
        'The full menu and in-theater service make it possible to combine happy hour, dinner, and a movie without moving the group between several locations. That convenience has helped turn Alamo into the tradition’s regular home.',
      ],
    },
    {
      heading: 'When a Movie Needs IMAX',
      paragraphs: [
        'Not every Movie Night takes place at Alamo.',
        'For movies that deserve the largest possible presentation, we go to the Airbus IMAX Theater at the Smithsonian’s Udvar-Hazy Center. Those outings trade Alamo’s bar and full menu for the scale and spectacle of a true IMAX experience.',
        'The venue depends on the movie. Alamo is the comfortable default, built around time together before and during the screening. Udvar-Hazy is reserved for the releases that call for an enormous screen and a theater capable of making the movie feel like an event.',
        'The existing list of Movie Nights tells the story of Al continuing to find reasons for us to return.',
      ],
    },
    {
      heading: 'Reckon Family Night',
      paragraphs: [
        'Movie Night is often more than a team event.',
        'When the film is age-appropriate, spouses, partners, and children join us. A night that began with coworkers meeting at the bar becomes an opportunity for Reckon families to spend time together.',
        'That wider invitation has helped the tradition grow beyond its original premise. The first outing happened because “Reckon” appeared in a movie title. The nights continued because they offered an easy, relaxed way to gather outside work.',
        'There is no complicated formula behind Movie Night. Al suggests something worth seeing, we choose between Alamo and IMAX, Matt waits for us at the bar, and the rest of the crew eventually makes it through traffic.',
        'Then the lights go down, the food arrives, and we watch the movie together.',
      ],
    },
  ],
  cadence: 'recurring',
  status: 'active',
  startDate: '2023-07-18',
  occurrenceCount: 12,
  nextMilestone: 15,
  defaultPlace: {
    name: 'Alamo Drafthouse One Loudoun',
    city: 'Ashburn',
    state: 'Virginia',
    description: 'Alamo Drafthouse at One Loudoun is Reckon’s go-to theater for Movie Night. The reason is straightforward: it combines a movie, drinks, and a full meal in one place. We can meet at the bar before showtime, reserve enough seats for the crew and their families, and continue ordering throughout the film without coordinating a separate dinner.',
    storySections: [
      {
        heading: 'More Than a Movie Theater',
        paragraphs: [
          'Alamo became the regular home of Reckon Movie Night with our first outing to see Mission: Impossible – Dead Reckoning.',
          'The title gave Al the excuse to organize the event. The experience at Alamo gave us a reason to keep coming back.',
          'The main attraction is not simply the movie screen. It is the food and drink service that turns a screening into a complete evening. Instead of asking a large group to agree on a restaurant, finish dinner on time, and then move everyone into a theater, we can do everything in one place.',
          'Everyone can choose a full meal, order the drinks they want, and continue ordering from their seat as the movie plays.',
        ],
      },
      {
        heading: 'An Hour at the Bar',
        paragraphs: [
          'Our Alamo nights generally begin an hour before the scheduled showtime.',
          'We meet at the bar, order food and drinks, and wait for everyone to make their way through Northern Virginia traffic. The running joke is that Matt arrives first and finds himself sitting in an empty bar while the rest of us are still on the road.',
          'Eventually, the group comes together.',
          'We stay at the bar until roughly ten minutes before the movie begins. That gives us time to talk before heading into the theater, finding our seats, and placing our initial food orders before the feature starts.',
        ],
      },
      {
        heading: 'Two Rows for Reckon',
        paragraphs: [
          'For a typical Movie Night, we reserve at least two full rows of seats.',
          'The size of the reservation reflects how the tradition has grown. These are not always team-only events. When the movie is age-appropriate, spouses, partners, and children join us too.',
          'Reserving full rows keeps the group together without requiring everyone to arrive at precisely the same moment or negotiate seats after entering the theater. By the time the lights go down, Reckon has claimed its own small section of the auditorium.',
        ],
      },
      {
        heading: 'Dinner from Your Seat',
        paragraphs: [
          'Each seat has access to a narrow table rail. To order during the movie, you write down what you want and press the small call button in front of you.',
          'A server collects the order, and the food or drinks arrive without anyone leaving the theater.',
          'That simple system is the feature that makes Alamo work particularly well for us. People can order what they want when they want it. Someone who ate at the bar can order another drink. Someone arriving hungry can have a full meal. There is no need to pause the evening or make the entire group follow the same schedule.',
          'For a crew large enough to occupy two rows, that flexibility matters.',
        ],
      },
      {
        heading: 'The Default Choice',
        paragraphs: [
          'Not every Reckon Movie Night happens at Alamo. When a release feels truly worthy of IMAX, we go to the Airbus IMAX Theater at the Udvar-Hazy Center.',
          'For almost everything else, Alamo is the default.',
          'Udvar-Hazy offers scale and spectacle. Alamo offers a full evening: time together at the bar, a movie, dinner, drinks, and enough reserved seats for the wider Reckon group.',
          'It is the place that helped turn one movie with “Reckon” in its title into a recurring tradition.',
        ],
      },
    ],
  },
  heroImage: movieNightImage,
  occurrences: [
    {
      id: 'movie-night-mission-impossible-dead-reckoning',
      date: '2023-07-18',
      title: 'Mission: Impossible – Dead Reckoning Part One',
      place: { name: 'Alamo Drafthouse One Loudoun', city: 'Ashburn', state: 'VA' },
      summary: 'Ethan Hunt races across the world to keep a rogue AI from deciding humanity’s future.',
    },
    {
      id: 'movie-night-the-fall-guy',
      date: '2024-05-21',
      title: 'The Fall Guy',
      place: { name: 'Alamo Drafthouse One Loudoun', city: 'Ashburn', state: 'VA' },
      summary: 'A battle-scarred stuntman hunts for a missing movie star and another shot with the woman he loves.',
    },
    {
      id: 'movie-night-despicable-me-4',
      date: '2024-07-23',
      title: 'Despicable Me 4',
      place: { name: 'Alamo Drafthouse One Loudoun', city: 'Ashburn', state: 'VA' },
      summary: 'Gru faces a revenge-hungry supervillain while his family adjusts to one mischievous new member.',
    },
    {
      id: 'movie-night-captain-america-brave-new-world',
      date: '2025-02-18',
      title: 'Captain America: Brave New World',
      place: { name: 'Alamo Drafthouse One Loudoun', city: 'Ashburn', state: 'VA' },
      summary: 'Sam Wilson steps fully into the shield while unraveling a conspiracy with global consequences.',
    },
    {
      id: 'movie-night-thunderbolts',
      date: '2025-05-06',
      title: 'Thunderbolts*',
      place: { name: 'Alamo Drafthouse One Loudoun', city: 'Ashburn', state: 'VA' },
      summary: 'A crew of Marvel misfits gets trapped in a deadly mission and discovers they may be stronger together.',
    },
    {
      id: 'movie-night-mission-impossible-the-final-reckoning',
      date: '2025-05-27',
      title: 'Mission: Impossible – The Final Reckoning',
      place: { name: 'Alamo Drafthouse One Loudoun', city: 'Ashburn', state: 'VA' },
      summary: 'Ethan Hunt makes one last impossible run to stop the Entity before it controls the world.',
    },
    {
      id: 'movie-night-jurassic-world-rebirth',
      date: '2025-07-15',
      title: 'Jurassic World Rebirth',
      place: { name: 'Alamo Drafthouse One Loudoun', city: 'Ashburn', state: 'VA' },
      summary: 'A covert team ventures into dinosaur territory to recover DNA that could change modern medicine.',
    },
    {
      id: 'movie-night-fantastic-four',
      date: '2025-07-31',
      title: 'Fantastic Four',
      place: {
        name: 'Airbus IMAX Theater at Udvar-Hazy Center',
        city: 'Chantilly',
        state: 'Virginia',
        description: 'When a movie calls for something bigger than a normal theater, the Reckon crew heads to the Airbus IMAX Theater at the Smithsonian’s Udvar-Hazy Center. The location is easy, the seats can be reserved in advance, and the scale of the screen and sound makes the right film feel like an event.',
        storySections: [
          {
            heading: 'When a Movie Is IMAX Worthy',
            paragraphs: [
              'Alamo Drafthouse is our usual home for Reckon Movie Night. Its bar, full menu, and in-seat service make it easy to combine drinks, dinner, and a movie in one place.',
              'But some movies need a different kind of theater.',
              'When a release feels truly “IMAX worthy,” we trade Alamo’s food and drink options for the size, scale, and sound of the Airbus IMAX Theater at Udvar-Hazy.',
              'There is no complicated definition of an IMAX-worthy movie. It is usually something designed for spectacle: a film that benefits from being seen on the largest screen available, with sound capable of making the experience feel physically bigger.',
              'For those movies, convenience at the seat matters less than what happens on the screen.',
            ],
          },
          {
            heading: 'An Easy Choice',
            paragraphs: [
              'Udvar-Hazy works for Reckon because the location is easy for the group and seats can be reserved in advance.',
              'Reserved seating is especially important when Movie Night grows beyond the immediate team. If the film is appropriate for a wider audience, spouses, partners, and children may join us too.',
              'Knowing where everyone will sit removes the uncertainty that can come with taking a large group to a popular screening. We can book the seats, arrive knowing the group will be together, and focus on the movie.',
            ],
          },
          {
            heading: 'Before the Movie Starts',
            paragraphs: [
              'The concessions are more traditional than Alamo’s full menu.',
              'There is popcorn, candy, soda, and the other familiar movie-theater options. Beer is available as well.',
              'The main difference is timing.',
              'At Alamo, we can write an order from our seats, press a call button, and continue ordering while the movie plays. At Udvar-Hazy, anything we want must be purchased before entering the auditorium.',
              'Once the movie begins, the focus remains on the screen.',
            ],
          },
          {
            heading: 'Size, Scale, and Sound',
            paragraphs: [
              'That screen is the reason we come.',
              'The Airbus IMAX Theater offers the kind of size and scale that changes how the right movie feels. The image fills the field of view, the sound surrounds the audience, and sequences designed for a large-format presentation have the space and power they were meant to have.',
              'It is not our default theater, and it does not need to be.',
              'Alamo remains the better choice when Movie Night is built around meeting at the bar, ordering dinner, and spending time together before and during the film. Udvar-Hazy serves a different purpose.',
              'When the movie itself is the event, we reserve our seats, buy the popcorn and beer before showtime, and let IMAX do the rest.',
            ],
          },
        ],
      },
      summary: 'Marvel’s first family must protect its retro-futuristic world from a planet-sized cosmic threat.',
    },
    {
      id: 'movie-night-project-hail-mary',
      date: '2026-03-19',
      title: 'Project Hail Mary',
      place: { name: 'Alamo Drafthouse One Loudoun', city: 'Ashburn', state: 'VA' },
      summary: 'A lone astronaut wakes light-years from home and finds Earth’s fate tied to an unlikely friendship.',
    },
    {
      id: 'movie-night-the-mandalorian-and-grogu',
      date: '2026-05-28',
      title: 'Star Wars: The Mandalorian and Grogu',
      place: { name: 'Alamo Drafthouse One Loudoun', city: 'Ashburn', state: 'VA' },
      summary: 'Din Djarin and Grogu take their found-family act on one more high-stakes trip across the galaxy.',
    },
    {
      id: 'movie-night-toy-story-5',
      date: '2026-06-23',
      title: 'Toy Story 5',
      place: { name: 'Alamo Drafthouse One Loudoun', city: 'Ashburn', state: 'VA' },
      summary: 'Woody, Buzz, and the gang face their newest rival for a child’s attention: technology.',
    },
    {
      id: 'movie-night-spider-man-brand-new-day',
      date: '2026-08-04',
      title: 'Spider-Man: Brand New Day',
      place: { name: 'Alamo Drafthouse One Loudoun', city: 'Ashburn', state: 'VA' },
      summary: 'With the world forgetting Peter Parker, Spider-Man faces a new threat no one can even see.',
    },
  ],
  occurrenceSectionKicker: 'Twelve Movies and Counting',
  occurrenceSectionTitle: 'The Movie Night Archive',
  tags: ['movies', 'team-connection', 'indoor', 'local-event'],
  meritBadgeIds: [],
  featured: true,
};

export const traditions = [reckonBall, anniversaryDinner, chickFilAFriday, happyHours, endOfYearLunch, newEmployeeLunch, movieNight];
export const traditionsBySlug = traditions.reduce((lookup, tradition) => ({ ...lookup, [tradition.slug]: tradition }), {
  'end-of-year-dinner': endOfYearLunch,
});

export default traditions;
